import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, DollarSign, CreditCard, Smartphone, Building2, Banknote, Eye, Download, ChevronDown, ChevronUp, Filter, RefreshCw, TrendingUp, Calendar, X, MoreVertical } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PAYMENT_MODE_CONFIG = {
  cash: { icon: Banknote, label: 'Cash', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  upi: { icon: Smartphone, label: 'UPI', bg: 'bg-purple-100', text: 'text-purple-700' },
  bank_transfer: { icon: Building2, label: 'Bank', bg: 'bg-blue-100', text: 'text-blue-700' },
  card: { icon: CreditCard, label: 'Card', bg: 'bg-amber-100', text: 'text-amber-700' },
  cheque: { icon: FileText, label: 'Cheque', bg: 'bg-slate-100', text: 'text-slate-700' },
  wallet: { icon: DollarSign, label: 'Wallet', bg: 'bg-cyan-100', text: 'text-cyan-700' },
  other: { icon: DollarSign, label: 'Other', bg: 'bg-gray-100', text: 'text-gray-700' }
};

// STRICT: Payment transaction status (separate from bill status)
const PAYMENT_STATUS_CONFIG = {
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completed' },
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
  voided: { bg: 'bg-red-100', text: 'text-red-700', label: 'Voided' }
};

// STRICT: Bill payment status (based on actual invoice balance)
const BILL_STATUS_CONFIG = {
  unpaid: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Unpaid' },
  partially_paid: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Partially Paid' },
  paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
  overdue: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' }
};

// Calculate bill status from actual invoice data - NOT from allocation_status
const getBillStatus = (alloc) => {
  if (!alloc) return null;
  const invoiceTotal = alloc.invoice_total || 0;
  const balanceBefore = alloc.balance_before || 0;
  const paidThisTime = alloc.allocated_amount || 0;
  const billBalanceLeft = Math.max(0, balanceBefore - paidThisTime);
  
  // Calculate total paid so far for this bill
  const paidSoFar = invoiceTotal - billBalanceLeft;
  
  if (billBalanceLeft <= 0) return 'paid';
  if (paidSoFar > 0) return 'partially_paid';
  return 'unpaid';
};

// Field accessors with fallbacks (real DB cols -> frontend names)
const getPaymentNo = (p) => p.payment_no || p.payment_number || '';
const getSupplierName = (p) => p.supplier_name || p.party_name || '';
const getSupplierId = (p) => p.supplier_id || p.party_id || '';
const getAllocations = (p) => p.allocations || p.invoices_allocated || [];
const getTransRef = (p) => p.transaction_reference || p.reference_number || p.transaction_id || '';

const PaymentRow = ({ payment, index, onView, onDownload, onCancel, bid, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const modeConfig = PAYMENT_MODE_CONFIG[payment.payment_mode] || PAYMENT_MODE_CONFIG.other;
  const paymentStatusConfig = PAYMENT_STATUS_CONFIG[payment.status] || PAYMENT_STATUS_CONFIG.completed;
  const ModeIcon = modeConfig.icon;

  // Get primary allocation (linked bill)
  const allocs = getAllocations(payment);
  const primaryAlloc = allocs[0];
  const isAdvance = !allocs || allocs.length === 0;
  
  // STRICT: Calculate truthful bill values
  const billNo = primaryAlloc?.invoice_number || '—';
  const billTotal = primaryAlloc?.invoice_total || 0;
  const paidThisTime = primaryAlloc?.allocated_amount || payment.amount_paid;
  const billBalanceLeft = primaryAlloc ? Math.max(0, (primaryAlloc.balance_before || 0) - paidThisTime) : 0;
  const paidSoFar = billTotal - billBalanceLeft;
  const billStatus = getBillStatus(primaryAlloc);
  const billStatusConfig = billStatus ? BILL_STATUS_CONFIG[billStatus] : null;

  return (
    <>
      <tr 
        className={`tl-tr hover:bg-teal-50/40 cursor-pointer ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="tl-td" style={{ width: '3%' }}>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </td>
        <td className="tl-td" style={{ width: '8%' }}>
          <span className="tl-cell-primary text-[12px] font-semibold text-teal-600">{getPaymentNo(payment)}</span>
        </td>
        <td className="tl-td" style={{ width: '8%' }}>
          <p className="tl-cell-regular text-[12px]">
            {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
          </p>
        </td>
        <td className="tl-td" style={{ width: '12%' }}>
          <p className="tl-cell-regular text-[12px] font-medium text-slate-800">{getSupplierName(payment)}</p>
        </td>
        <td className="tl-td" style={{ width: '7%' }}>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${modeConfig.bg} ${modeConfig.text}`}>
            <ModeIcon className="w-3 h-3" /> {modeConfig.label}
          </span>
        </td>
        <td className="tl-td tl-td-right" style={{ width: '8%' }}>
          <p className="font-bold text-emerald-600 text-[13px]">₹{(payment.amount_paid || 0).toLocaleString('en-IN')}</p>
        </td>
        <td className="tl-td" style={{ width: '8%' }}>
          {isAdvance ? (
            <span className="text-[11px] text-amber-600 font-medium italic">No Bill Linked</span>
          ) : (
            <span 
              className="text-[12px] text-blue-600 font-medium cursor-pointer hover:underline" 
              onClick={(e) => { e.stopPropagation(); navigate(`/business/${bid}/purchase-invoices/${primaryAlloc.invoice_id}`); }}
            >
              {billNo}
            </span>
          )}
        </td>
        <td className="tl-td tl-td-right" style={{ width: '8%' }}>
          <p className="text-[12px] text-slate-700 font-medium">
            {isAdvance ? '—' : `₹${billTotal.toLocaleString('en-IN')}`}
          </p>
        </td>
        <td className="tl-td tl-td-right" style={{ width: '8%' }}>
          <p className="text-[12px] text-emerald-600 font-medium">
            {isAdvance ? '—' : `₹${paidSoFar.toLocaleString('en-IN')}`}
          </p>
        </td>
        <td className="tl-td tl-td-right" style={{ width: '8%' }}>
          <p className={`font-semibold text-[12px] ${billBalanceLeft > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
            {isAdvance ? '—' : `₹${billBalanceLeft.toLocaleString('en-IN')}`}
          </p>
        </td>
        <td className="tl-td" style={{ width: '9%' }}>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${paymentStatusConfig.bg} ${paymentStatusConfig.text}`}>
            {paymentStatusConfig.label}
          </span>
        </td>
        <td className="tl-td" style={{ width: '5%' }} onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onView(payment)}>
                <Eye className="w-4 h-4 mr-2" /> View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDownload(payment.id, getPaymentNo(payment))}>
                <Download className="w-4 h-4 mr-2" /> Download Voucher
              </DropdownMenuItem>
              {payment.status === 'completed' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onCancel(payment)} className="text-red-600 focus:text-red-600">
                    <X className="w-4 h-4 mr-2" /> Cancel Payment
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
      
      {/* Expanded Row - TRUTHFUL DATA */}
      {expanded && (
        <tr className="bg-gradient-to-r from-teal-50/50 to-slate-50/50">
          <td colSpan={12} className="px-6 py-4">
            <div className="grid grid-cols-3 gap-5">
              
              {/* Bill Details - TRUTHFUL */}
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <p className="text-[10px] text-teal-600 uppercase font-bold tracking-wider mb-3">Linked Bill Details</p>
                {isAdvance ? (
                  <div className="text-center py-4">
                    <p className="text-amber-600 font-semibold">Extra Paid (Advance)</p>
                    <p className="text-sm text-slate-500 mt-1">No bill was linked to this payment</p>
                    <p className="text-xl font-bold text-amber-600 mt-2">₹{(payment.amount_paid || 0).toLocaleString('en-IN')}</p>
                  </div>
                ) : (
                  <div className="space-y-2.5 text-[12px]">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Bill #:</span>
                      <span className="text-blue-600 font-semibold cursor-pointer hover:underline" onClick={() => navigate(`/business/${bid}/purchase-invoices/${primaryAlloc.invoice_id}`)}>
                        {billNo}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Bill Total:</span>
                      <span className="text-slate-800 font-semibold">₹{billTotal.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Paid For This Bill:</span>
                      <span className="text-teal-600 font-bold">₹{paidThisTime.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Paid So Far:</span>
                      <span className="text-emerald-600 font-semibold">₹{paidSoFar.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-slate-200">
                      <span className="text-slate-700 font-medium">Bill Balance Left:</span>
                      <span className={`font-bold text-base ${billBalanceLeft > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        ₹{billBalanceLeft.toLocaleString('en-IN')}
                      </span>
                    </div>
                    {billStatusConfig && (
                      <div className="flex justify-between items-center pt-1">
                        <span className="text-slate-500">Bill Status:</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${billStatusConfig.bg} ${billStatusConfig.text}`}>
                          {billStatusConfig.label}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Payment Details */}
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <p className="text-[10px] text-teal-600 uppercase font-bold tracking-wider mb-3">Payment Details</p>
                <div className="space-y-2.5 text-[12px]">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Amount Paid:</span>
                    <span className="text-emerald-600 font-bold text-base">₹{(payment.amount_paid || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Payment Mode:</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${modeConfig.bg} ${modeConfig.text}`}>{modeConfig.label}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Paid From:</span>
                    <span className="text-slate-800">{payment.debit_from_account || 'Cash'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Paid By:</span>
                    <span className="text-slate-800">{payment.paid_by_name || '—'}</span>
                  </div>
                  {getTransRef(payment) && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Reference:</span>
                      <span className="text-slate-800 font-medium">{getTransRef(payment)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-slate-500">Payment Status:</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${paymentStatusConfig.bg} ${paymentStatusConfig.text}`}>
                      {paymentStatusConfig.label}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <p className="text-[10px] text-teal-600 uppercase font-bold tracking-wider mb-3">Actions</p>
                <div className="space-y-2">
                  <button onClick={() => onView(payment)} className="w-full text-left text-[12px] text-teal-600 hover:text-teal-700 font-medium py-2.5 px-3 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors">
                    → View Payment Details
                  </button>
                  {!isAdvance && billBalanceLeft > 0 && payment.status === 'completed' && (
                    <button 
                      onClick={() => navigate(`/business/${bid}/payments-made/new?supplier_id=${getSupplierId(payment)}&invoice_id=${primaryAlloc.invoice_id}`)} 
                      className="w-full text-left text-[12px] text-emerald-600 hover:text-emerald-700 font-medium py-2.5 px-3 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
                    >
                      → Add Payment (₹{billBalanceLeft.toLocaleString('en-IN')} left)
                    </button>
                  )}
                  {!isAdvance && (
                    <button 
                      onClick={() => navigate(`/business/${bid}/purchase-invoices/${primaryAlloc.invoice_id}`)} 
                      className="w-full text-left text-[12px] text-blue-600 hover:text-blue-700 font-medium py-2.5 px-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      → View Bill & Payment History
                    </button>
                  )}
                  <button onClick={() => onDownload(payment.id, getPaymentNo(payment))} className="w-full text-left text-[12px] text-slate-600 hover:text-slate-700 font-medium py-2.5 px-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    → Download Payment Voucher
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const PaymentsMadePage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelPayment, setCancelPayment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  
  // Collapsible header state
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(() => {
    const saved = localStorage.getItem('header_collapsed_payments_made');
    return saved === 'true';
  });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paidFromFilter, setPaidFromFilter] = useState('all');

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    localStorage.setItem('header_collapsed_payments_made', String(isHeaderCollapsed));
  }, [isHeaderCollapsed]);

  // Date range presets
  const applyDatePreset = (preset) => {
    const today = new Date();
    let from = '', to = today.toISOString().split('T')[0];
    
    switch (preset) {
      case 'today':
        from = to;
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        from = weekAgo.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        from = monthAgo.toISOString().split('T')[0];
        break;
      case 'custom':
        // Keep existing custom dates
        return;
      default:
        from = '';
        to = '';
    }
    setDateFrom(from);
    setDateTo(to);
    setDateRange(preset);
  };

  const fetchPayments = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (modeFilter !== 'all') params.append('payment_mode', modeFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (dateFrom) params.append('from_date', dateFrom);
      if (dateTo) params.append('to_date', dateTo);
      if (paymentStatusFilter !== 'all') params.append('status', paymentStatusFilter);

      const response = await api.get(`/payments-made?${params}`);
      const data = response.data;
      let filteredPayments = data.payments || [];

      // Client-side filters
      if (paidFromFilter !== 'all') {
        filteredPayments = filteredPayments.filter(p => p.debit_from_account?.toLowerCase().includes(paidFromFilter));
      }

      setPayments(filteredPayments);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [bid, paymentStatusFilter, modeFilter, searchQuery, dateFrom, dateTo, paidFromFilter]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleView = (payment) => navigate(`/business/${bid}/payments-made/${payment.id}`);

  const handleDownload = async (id, paymentNo) => {
    try {
      const response = await api.get(`/payments-made/${id}/voucher-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payment_${paymentNo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Voucher downloaded');
    } catch (error) {
      toast.error('Error downloading voucher');
    }
  };

  const handleCancel = (payment) => {
    setCancelPayment(payment);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Please enter a reason'); return; }
    try {
      await api.post(`/payments-made/${cancelPayment.id}/void`, {}, { params: { reason: cancelReason } });
      toast.success('Payment cancelled');
      fetchPayments();
      setShowCancelModal(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to cancel payment'));
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setPaymentStatusFilter('all');
    setModeFilter('all');
    setDateRange('all');
    setDateFrom('');
    setDateTo('');
    setPaidFromFilter('all');
  };

  const hasActiveFilters = paymentStatusFilter !== 'all' || modeFilter !== 'all' || dateRange !== 'all' || paidFromFilter !== 'all';

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  // Calculate KPIs
  const today = new Date().toISOString().split('T')[0];
  const todayPayments = payments.filter(p => p.payment_date === today && p.status === 'completed');
  const todayTotal = todayPayments.reduce((sum, p) => sum + (p.amount_paid || 0), 0);
  
  // Count bills status
  const partiallyPaidBills = new Set();
  const paidBills = new Set();
  payments.forEach(p => {
    if (p.status === 'completed' && p.allocations?.length > 0) {
      p.allocations.forEach(a => {
        const status = getBillStatus(a);
        if (status === 'partially_paid') partiallyPaidBills.add(a.invoice_id);
        else if (status === 'paid') paidBills.add(a.invoice_id);
      });
    }
  });

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* COLLAPSIBLE HEADER */}
      {isHeaderCollapsed ? (
        /* Collapsed State - Minimal Header */
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200 animate-fadeIn">
          <div className="flex items-center gap-4">
            <h1 className="text-[15px] font-medium text-slate-700">Payments Made</h1>
            <span className="text-[12px] text-slate-400">• {payments.length} payments</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/business/${bid}/payments-made/new`)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[12px] font-medium"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2} />
              Record Payment
            </button>
            <button
              onClick={() => setIsHeaderCollapsed(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
            >
              <ChevronDown className="w-3.5 h-3.5" />
              Expand
            </button>
          </div>
        </div>
      ) : (
        /* Expanded State - Full Header */
        <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4 relative">
          <button
            onClick={() => setIsHeaderCollapsed(true)}
            className="absolute top-3 right-6 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-400 hover:text-slate-600 hover:bg-slate-100/80 rounded-lg transition-all"
          >
            <ChevronUp className="w-3.5 h-3.5" />
            Collapse
          </button>
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Payments Made</h1>
                <p className="text-[13px] text-slate-500 mt-0.5 font-normal">Track all supplier payment transactions</p>
              </div>
              <button
                onClick={() => navigate(`/business/${bid}/payments-made/new`)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[13px] font-medium shadow-sm mr-24"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                Record Payment
              </button>
            </div>

          {/* KPI Stats - Premium Cards */}
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Total Paid</p>
                <p className="text-lg font-bold text-teal-600">{formatCompactCurrency(stats.total_paid || 0)}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Today</p>
                <p className="text-lg font-bold text-blue-600">{formatCompactCurrency(todayTotal)}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Banknote className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Cash</p>
                <p className="text-lg font-bold text-slate-700">{formatCompactCurrency(stats.cash_total || 0)}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">UPI</p>
                <p className="text-lg font-bold text-slate-700">{formatCompactCurrency(stats.upi_total || 0)}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Partial Bills</p>
                <p className="text-lg font-bold text-amber-600">{partiallyPaidBills.size}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Paid Bills</p>
                <p className="text-lg font-bold text-emerald-600">{paidBills.size}</p>
              </div>
            </div>
          </div>

          {/* Search & Filters - Premium */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 relative max-w-md">
                <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by payment #, supplier, bill #..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white rounded-lg text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:outline-none placeholder:text-slate-400 shadow-sm border border-slate-200"
                />
              </div>

              {/* Date Range Dropdown */}
              <select
                value={dateRange}
                onChange={(e) => applyDatePreset(e.target.value)}
                className="px-3 py-2.5 bg-white rounded-lg text-[13px] text-slate-700 border border-slate-200 shadow-sm focus:ring-2 focus:ring-teal-500/20"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </select>

              <select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                className="px-3 py-2.5 bg-white rounded-lg text-[13px] text-slate-700 border border-slate-200 shadow-sm focus:ring-2 focus:ring-teal-500/20"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="draft">Draft</option>
                <option value="voided">Cancelled</option>
              </select>

              <select
                value={modeFilter}
                onChange={(e) => setModeFilter(e.target.value)}
                className="px-3 py-2.5 bg-white rounded-lg text-[13px] text-slate-700 border border-slate-200 shadow-sm focus:ring-2 focus:ring-teal-500/20"
              >
                <option value="all">All Modes</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
              </select>

              <select
                value={paidFromFilter}
                onChange={(e) => setPaidFromFilter(e.target.value)}
                className="px-3 py-2.5 bg-white rounded-lg text-[13px] text-slate-700 border border-slate-200 shadow-sm focus:ring-2 focus:ring-teal-500/20"
              >
                <option value="all">All Accounts</option>
                <option value="cash">Cash Account</option>
                <option value="bank">Bank Account</option>
              </select>

              {hasActiveFilters && (
                <button onClick={clearFilters} className="inline-flex items-center gap-1.5 px-3 py-2.5 text-slate-500 hover:text-slate-700 text-[13px] font-medium bg-white rounded-lg border border-slate-200 shadow-sm">
                  <RefreshCw className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>

            {/* Custom Date Range */}
            {dateRange === 'custom' && (
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-slate-200">
                <Calendar className="w-4 h-4 text-slate-400" />
                <label className="text-[12px] text-slate-500 font-medium">From:</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-2.5 py-1.5 bg-slate-50 rounded-md text-[12px] text-slate-700 border border-slate-200" />
                <label className="text-[12px] text-slate-500 font-medium">To:</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-2.5 py-1.5 bg-slate-50 rounded-md text-[12px] text-slate-700 border border-slate-200" />
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* TABLE */}
      <div className="flex-1 overflow-hidden px-6 pb-5">
        <div className="max-w-[1600px] mx-auto h-full flex flex-col">
          <div className="tl-table-container flex-1 flex flex-col overflow-hidden">
            
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              </div>
            ) : payments.length === 0 ? (
              <div className="tl-empty-state flex-1 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-4">
                  <DollarSign className="w-8 h-8 text-teal-500" />
                </div>
                <p className="tl-empty-title text-lg font-semibold text-slate-700">{searchQuery || hasActiveFilters ? 'No payments found' : 'No payments recorded yet'}</p>
                <p className="tl-empty-description mt-1 mb-5 text-slate-500">{searchQuery || hasActiveFilters ? 'Try adjusting your filters' : 'Record your first payment to a supplier'}</p>
                {!searchQuery && !hasActiveFilters && (
                  <button onClick={() => navigate(`/business/${bid}/payments-made/new`)} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-[13px] font-medium shadow-sm">
                    Record Payment
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Table Header */}
                <div className="flex-shrink-0">
                  <table className="tl-table">
                    <thead>
                      <tr>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200" style={{ width: '3%' }}></th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200" style={{ width: '8%' }}>Payment #</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200" style={{ width: '8%' }}>Date</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200" style={{ width: '12%' }}>Supplier</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200" style={{ width: '7%' }}>Mode</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200 tl-th-right" style={{ width: '8%' }}>Amount Paid</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200" style={{ width: '8%' }}>Linked Bill #</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200 tl-th-right" style={{ width: '8%' }}>Bill Total</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200 tl-th-right" style={{ width: '8%' }}>Paid So Far</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200 tl-th-right" style={{ width: '8%' }}>Bill Balance Left</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200" style={{ width: '9%' }}>Status</th>
                        <th className="tl-th bg-teal-50/70 text-teal-700 border-b border-teal-200" style={{ width: '7%' }}>Actions</th>
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* Table Body */}
                <div className="flex-1 overflow-y-auto">
                  <table className="tl-table">
                    <tbody>
                      {payments.map((payment, index) => (
                        <PaymentRow
                          key={payment.id}
                          payment={payment}
                          index={index}
                          onView={handleView}
                          onDownload={handleDownload}
                          onCancel={handleCancel}
                          bid={bid}
                          navigate={navigate}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Table Footer */}
                <div className="tl-table-footer flex items-center justify-between py-3 px-4 bg-white border-t border-slate-200">
                  <p className="text-[13px] text-slate-600">Showing <span className="font-semibold text-slate-800">{payments.length}</span> payments</p>
                  <p className="text-[13px] text-slate-600">Total Paid: <span className="font-bold text-teal-600">{formatCompactCurrency(stats.total_paid || 0)}</span></p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && cancelPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Cancel Payment</h3>
            <p className="text-sm text-slate-500 mb-4">This will reverse {getPaymentNo(cancelPayment)} and update the bill balance.</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter reason for cancellation..."
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowCancelModal(false)} className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium">Keep Payment</button>
              <button onClick={confirmCancel} className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium">Cancel Payment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsMadePage;
